import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Scissors, Plus, ChevronDown, Disc, Printer, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtQty, fmtDT, todayIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Combobox, StatusBadge, Empty, tableWrap, th, td, num } from "@/components/common";
import { AttachmentPicker } from "@/pages/InwardNew";
import { cn } from "@/lib/utils";
import { SizeQuickPick } from "@/components/SizePicker";
import { ScanButton } from "@/components/QrScanner";
import { MicButton } from "@/components/VoiceInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";

const PRESETS = [5, 10, 25, 50, 100];
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const BLANK = { cut: "", offcut: "", waste: "", bill_no: "", bill_pending: false, party_name: "", taken_by: "", vehicle_no: "", mobile: "", remarks: "" };

function DrumPicker({ sources, value, onChange }) {
  if (!sources.length) return <div className="text-sm text-muted-foreground py-3" data-testid="cutting-no-drums">No drums with available balance for this size.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="cutting-drum-list">
      {sources.map((d) => (
        <button type="button" key={d.id} onClick={() => onChange(d)} data-testid={`cutting-drum-${d.drum_code}`}
          className={cn("text-left rounded-md border p-2.5 transition-colors", value?.id === d.id ? "border-red-600 bg-red-600/5 ring-1 ring-red-600" : "hover:border-primary/50")}>
          <div className="flex items-center gap-1 font-mono text-xs font-semibold"><Disc className="h-3 w-3" />{d.drum_code}{d.kind === "offcut" && <span className="ml-1 text-[9px] uppercase text-amber-600">offcut</span>}</div>
          <div className="text-[11px] text-muted-foreground truncate">Mfr {d.mfr_drum_no || "—"}</div>
          <div className="font-mono text-base font-semibold mt-1">{fmtQty(d.available, d.uom)}</div>
        </button>
      ))}
    </div>
  );
}

function TodayList({ items }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Today's cutting · {fmtQty(items.filter((i) => i.status !== "reversed").reduce((s, i) => s + i.cut_length, 0))}</div>
      <div className={tableWrap}>
        <table className="w-full" data-testid="cutting-today-table">
          <thead><tr>{["Time", "Slip", "Bill", "Brand / Size", "Drum", "Cut", "Drum Bal.", "Taken By", "Status"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id} className="hover:bg-muted/40">
              <td className={td}>{fmtDT(c.at).split(", ").pop()}</td>
              <td className={`${td} font-mono`}><Link to={`/cutting/${c.id}`} className="hover:underline" data-testid={`cutting-link-${c.slip_no}`}>{c.slip_no}</Link></td>
              <td className={`${td} font-mono`}>{c.bill_no ? <a href={`/print/bill/x?no=${encodeURIComponent(c.bill_no)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" title="Print all cables on this bill" data-testid={`cutting-bill-print-${c.slip_no}`}><Printer className="h-3 w-3" />{c.bill_no}</a> : "—"}</td><td className={td}>{c.brand} · {c.cable_size}</td><td className={`${td} font-mono`}>{c.drum_code}</td>
              <td className={`${td} ${num}`}>{fmtQty(c.cut_length, c.uom)}</td><td className={`${td} ${num}`}>{fmtQty(c.drum_balance_after, c.uom)}</td><td className={td}>{c.taken_by}</td>
              <td className={td}><StatusBadge status={c.status} /></td>
            </tr>
          ))}</tbody>
        </table>
        {!items.length && <Empty text="No cutting entries today" />}
      </div>
    </div>
  );
}

function CutLine({ l, i, left, total, set, remove }) {
  const d = l.drum;
  const code = d.mfr_drum_no || d.parent_mfr_drum_no || d.drum_code;
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`cut-line-${i}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="font-semibold truncate">{i + 1}. {d.brand} · {d.cable_size}</div><div className="text-xs text-muted-foreground font-mono truncate">{code} · {d.product_name}</div></div>
        <Button size="icon" variant="ghost" onClick={remove} title="Remove" data-testid={`cut-line-remove-${i}`}><X className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center">
        <Input type="number" inputMode="decimal" step="0.001" min="0" placeholder="Cut length (m)" value={l.cut} onChange={(e) => set("cut", e.target.value)} className="h-12 text-xl font-mono" data-testid={`cut-line-length-${i}`} autoFocus />
        <div className={cn("rounded-md px-3 py-1.5 font-mono text-right", left < 0 ? "bg-red-600/10 text-red-600" : "bg-muted")} data-testid={`cut-line-left-${i}`}>
          <div className="text-[10px] font-sans text-muted-foreground">{fmtQty(d.available, d.uom)} − {fmtQty(total, d.uom)}</div><div className="font-semibold">= {fmtQty(left, d.uom)}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{PRESETS.map((p) => <Button key={p} type="button" variant="outline" size="sm" onClick={() => set("cut", String(Math.round(((+l.cut || 0) + p) * 1000) / 1000))} data-testid={`cut-line-preset-${i}-${p}`}>+{p}</Button>)}
        <button type="button" className="text-xs flex items-center gap-1 text-muted-foreground ml-auto" onClick={() => set("more", !l.more)} data-testid={`cut-line-more-${i}`}><ChevronDown className="h-3 w-3" />Offcut / scrap</button></div>
      {l.more && <div className="grid grid-cols-2 gap-3 max-w-md">
        <Input type="number" step="0.001" min="0" placeholder="Reusable offcut (m)" value={l.offcut} onChange={(e) => set("offcut", e.target.value)} data-testid={`cut-line-offcut-${i}`} />
        <Input type="number" step="0.001" min="0" placeholder="Unusable scrap (m)" value={l.waste} onChange={(e) => set("waste", e.target.value)} data-testid={`cut-line-waste-${i}`} />
      </div>}
    </div>
  );
}

function AiEntry({ onFill }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/parse-cutting", { text });
      const picked = [];
      for (const l of data.lines) {
        const { data: src } = await api.get("/cutting/sources", { params: { cable_size: l.cable_size } });
        const pool = src.filter((s) => (!l.brand || s.brand === l.brand) && !picked.some((p) => p.id === s.id));
        const fit = pool.filter((s) => s.available >= +l.length).sort((a, b) => a.available - b.available)[0];
        if (fit) picked.push({ ...fit, _cut: String(l.length) }); else toast.error(`No ${l.brand} ${l.cable_size} piece has ${l.length} m`);
      }
      onFill(data, picked);
      toast.success(`Filled: ${picked.map((p) => `${p._cut} m ${p.product_name} (${p.mfr_drum_no || p.drum_code})`).join("; ") || "details only"}. Change any drum before submitting.`, { duration: 8000 });
      setOpen(false); setText("");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid="cutting-ai-entry-btn"><Sparkles className="h-4 w-4 mr-1 text-red-600" />Speak / type entry</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md" data-testid="cutting-ai-dialog">
          <DialogHeader><DialogTitle>Say or type the cutting</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Example: “Bill 501, Sharma Electricals, 25 metre 6 sq mm Polycab and 10 metre 4 sq mm KEI, Ravi taken, vehicle KA01AB1234”</p>
          <div className="flex gap-2 items-start"><Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid="cutting-ai-text" /><MicButton onText={(t) => setText((p) => `${p} ${t}`.trim())} testid="cutting-ai-mic" /></div>
          <Button disabled={busy || text.trim().length < 3} onClick={go} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cutting-ai-fill-btn">{busy ? "Reading…" : "Fill the form"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Cutting() {
  const { lookups, can, user } = useAuth();
  const [brand, setBrand] = useState("");
  const [options, setOptions] = useState([]);
  const [productId, setProductId] = useState("");
  const [sources, setSources] = useState([]);
  const [lines, setLines] = useState([]);
  const [f, setF] = useState(BLANK);
  const [cutBy, setCutBy] = useState(user?.name || "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [today, setToday] = useState([]);
  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const addLines = (ds) => {
    const have = new Set(lines.map((l) => l.drum.id));
    const add = ds.filter((d) => !have.has(d.id)).map((d) => ({ key: uuid(), drum: d, cut: d._cut || "", offcut: "", waste: "", more: false }));
    if (add.length < ds.length) toast.message("Drum already in the list");
    if (add.length) setLines([...lines, ...add]);
    setTimeout(() => document.querySelector("[data-testid='cutting-entry-section']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };
  const setLine = (key, k, v) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, [k]: v } : l)));
  const lineTotal = (l) => (+l.cut || 0) + (+l.offcut || 0) + (+l.waste || 0);
  const lineLeft = (l) => Math.round((l.drum.available - lineTotal(l)) * 1000) / 1000;

  const loadToday = () => api.get("/cuttings", { params: { date_from: todayIST(), date_to: todayIST() } }).then((r) => setToday(r.data));
  useEffect(() => {
    loadToday();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);
  useEffect(() => {
    if (brand) {
      setProductId("");
      api.get("/cutting/options", { params: { brand } }).then((r) => setOptions(r.data));
    }
  }, [brand]);
  const loadSources = (pid) => api.get("/cutting/sources", { params: { product_id: pid } }).then((r) => setSources(r.data));
  useEffect(() => { if (productId) loadSources(productId); }, [productId]);
  useEffect(() => { const t = setTimeout(() => api.get("/bills", { params: { q: f.bill_no } }).then((r) => setBills(r.data)), 250); return () => clearTimeout(t); }, [f.bill_no]);

  const opt = options.find((o) => o.product_id === productId);
  const over = lines.some((l) => lineLeft(l) < 0);
  const allCut = lines.length > 0 && lines.every((l) => +l.cut > 0);
  const grand = lines.reduce((s, l) => s + (+l.cut || 0), 0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickBill = (v) => {
    const b = bills.find((x) => x.bill_no === v.toUpperCase());
    setF({ ...f, bill_no: v, party_name: b && !f.party_name ? b.party_name : f.party_name });
  };

  const submit = async () => {
    setBusy(true);
    const cust = customers.find((c) => c.name.toLowerCase() === f.party_name.trim().toLowerCase());
    const saved = [];
    let left = [...lines];
    for (const [i, l] of lines.entries()) {
      try {
        const { data } = await api.post("/cuttings", {
          client_ref: l.key, drum_id: l.drum.id, cut_length: +l.cut, offcut_length: +(l.offcut || 0), waste_length: +(l.waste || 0),
          bill_no: f.bill_no, bill_pending: f.bill_pending, party_id: cust?.id || null, party_name: f.party_name, taken_by: f.taken_by,
          vehicle_no: f.vehicle_no, mobile: f.mobile, cut_by: cutBy, remarks: f.remarks, attachment_ids: i === 0 ? files.map((x) => x.id) : [],
        });
        saved.push(data);
        left = left.filter((x) => x.key !== l.key);
      } catch (e) { toast.error(`${l.drum.mfr_drum_no || l.drum.drum_code}: ${errMsg(e)}`); break; }
    }
    setLines(left);
    if (saved.length) {
      setDone({ items: saved, bill_no: f.bill_no });
      toast.success(`${saved.length} cable(s) cut and saved`);
      if (!left.length) { setF(BLANK); setFiles([]); }
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (productId) loadSources(productId);
      loadToday();
      if (brand) api.get("/cutting/options", { params: { brand } }).then((r) => setOptions(r.data));
    }
    setBusy(false);
  };

  const sizeOpts = useMemo(() => options.map((o) => ({ value: o.product_id, label: `${o.cable_size} · ${o.product_name} · ${fmtQty(o.available, o.uom)}` })), [options]);
  return (
    <div className="space-y-6 pb-24">
      <PageHeader title="Daily Cable Cutting" subtitle="Brand → size → drum → length. Stock updates the moment you submit." />
      {done && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 flex flex-wrap items-center gap-4" data-testid="cutting-success">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold" data-testid="cutting-success-slip">{done.items.length} cable(s) saved{done.bill_no ? ` · Bill ${done.bill_no}` : ""}</div>
            <div className="text-sm font-mono">{done.items.map((d) => `${d.slip_no}: ${fmtQty(d.cut_length, d.uom)} (${d.mfr_drum_no || d.drum_code} now ${fmtQty(d.drum_balance_after, d.uom)})`).join(" · ")}</div>
          </div>
          {done.bill_no && <Button variant="outline" onClick={() => window.open(`/print/bill/x?no=${encodeURIComponent(done.bill_no)}`, "_blank")} data-testid="cutting-success-bill-btn"><Printer className="h-4 w-4 mr-1" />Bill slip / WhatsApp</Button>}
        </div>
      )}
      <section className="rounded-lg border bg-card p-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          <ScanButton testid="cutting-scan-btn" onDrum={(d) => { if (!["drum", "offcut"].includes(d.kind) || d.available <= 0) { toast.error(`${d.drum_code} cannot be cut (no free balance)`); return; } setDone(null); addLines([d]); }} />
          <AiEntry onFill={(p, ds) => { setDone(null); setF((x) => ({ ...x, bill_no: p.bill_no.toUpperCase() || x.bill_no, party_name: p.party_name || x.party_name, taken_by: p.taken_by || x.taken_by, vehicle_no: p.vehicle_no || x.vehicle_no, mobile: p.mobile || x.mobile })); if (ds.length) addLines(ds); }} />
        </div>
        <Field label="Quick pick · select cable size to see every brand and drum"><SizeQuickPick onPick={(ds) => { setDone(null); addLines(ds); }} refreshKey={today.length} /></Field>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">or choose step by step</div>
        <Field label="1 · Brand" required>
          <div className="flex flex-wrap gap-2" data-testid="cutting-brand-chips">
            {(lookups?.brands || []).map((b) => (
              <button type="button" key={b} onClick={() => setBrand(b)} data-testid={`cutting-brand-${b.replace(/\s+/g, "-")}`}
                className={cn("h-10 px-4 rounded-full border text-sm font-medium transition-colors", brand === b ? "bg-[#0B1530] text-white border-[#0B1530] dark:bg-blue-500 dark:border-blue-500" : "hover:border-primary/50")}>{b}</button>
            ))}
          </div>
        </Field>
        {brand && <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end [&>*]:min-w-0">
          <Field label="2 · Cable size (stock for selected brand only)" required><Combobox options={sizeOpts} value={productId} onChange={setProductId} testid="cutting-size-select" placeholder={options.length ? "Search size" : "No stock for this brand"} /></Field>
          {opt && <div className="rounded-md bg-muted px-4 py-2 text-sm" data-testid="cutting-available-stock">Available <b className="font-mono">{fmtQty(opt.available, opt.uom)}</b> · {opt.drums} drum(s){opt.offcuts ? ` + ${opt.offcuts} offcut(s)` : ""}</div>}
        </div>}
        {productId && <Field label="3 · Drum"><DrumPicker sources={sources.filter((s) => !lines.some((l) => l.drum.id === s.id))} value={null} onChange={(d) => { setDone(null); addLines([d]); }} /></Field>}
      </section>

      {lines.length > 0 && <section className="rounded-lg border bg-card p-4 space-y-4" data-testid="cutting-entry-section">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">4 · Cables to cut ({lines.length})</div>
        {lines.map((l, i) => <CutLine key={l.key} l={l} i={i} left={lineLeft(l)} total={lineTotal(l)} set={(k, v) => setLine(l.key, k, v)} remove={() => setLines(lines.filter((x) => x.key !== l.key))} />)}
        <Button variant="outline" className="w-full border-dashed h-12" onClick={() => document.querySelector("[data-testid='cutting-quick-size-select']")?.scrollIntoView({ behavior: "smooth", block: "center" })} data-testid="cutting-add-more-btn"><Plus className="h-4 w-4 mr-1" />Do you want to add more cables?</Button>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Bill number" required>
            <Input list="bill-list" value={f.bill_pending ? "PENDING" : f.bill_no} disabled={f.bill_pending} onChange={(e) => pickBill(e.target.value)} placeholder="RC/105" data-testid="cutting-bill-input" />
            <datalist id="bill-list">{bills.map((b) => <option key={b.bill_no} value={b.bill_no}>{b.party_name}</option>)}</datalist>
            {can("cutting:pending_bill") && <label className="flex items-center gap-2 text-xs mt-1"><Checkbox checked={f.bill_pending} onCheckedChange={(v) => setF({ ...f, bill_pending: !!v })} data-testid="cutting-bill-pending-checkbox" />Bill pending (authorised)</label>}
          </Field>
          <Field label="Party name"><Input list="party-list" value={f.party_name} onChange={set("party_name")} data-testid="cutting-party-input" /><datalist id="party-list">{customers.map((c) => <option key={c.id} value={c.name} />)}</datalist></Field>
          <Field label="Taken by" required><Input list="emp-list" value={f.taken_by} onChange={set("taken_by")} placeholder="Employee or external person" data-testid="cutting-taken-by-input" /><datalist id="emp-list">{employees.map((e) => <option key={e.id} value={e.name} />)}</datalist></Field>
          <Field label="Vehicle number"><Input value={f.vehicle_no} onChange={(e) => setF({ ...f, vehicle_no: e.target.value.toUpperCase() })} placeholder="KA01AB1234" data-testid="cutting-vehicle-input" /></Field>
          <Field label="Mobile number"><Input type="tel" inputMode="numeric" value={f.mobile} onChange={set("mobile")} placeholder="98XXXXXXXX" data-testid="cutting-mobile-input" /></Field>
          <Field label="Cutting done by"><Input list="emp-list" value={cutBy} onChange={(e) => setCutBy(e.target.value)} data-testid="cutting-cut-by-input" /></Field>
          <Field label="Remarks" className="col-span-2 lg:col-span-3"><Input value={f.remarks} onChange={set("remarks")} data-testid="cutting-remarks-input" /></Field>
        </div>
        <Field label="Signed copy / bill / photo"><AttachmentPicker files={files} setFiles={setFiles} testid="cutting-attach" /></Field>
      </section>}

      {lines.length > 0 && <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-30 border-t bg-background/95 backdrop-blur px-4 sm:px-8 py-3 flex items-center gap-3 justify-end">
        <span className="text-sm font-mono mr-auto" data-testid="cutting-grand-total">{lines.length} cable(s) · {fmtQty(Math.round(grand * 1000) / 1000)}</span>
        <Button className="bg-red-600 hover:bg-red-700 text-white h-12 px-6 text-base" disabled={busy || over || !allCut || !f.taken_by || (!f.bill_no && !f.bill_pending)} onClick={submit} data-testid="cutting-submit-btn">
          <Scissors className="h-4 w-4 mr-2" />{busy ? "Saving…" : `Submit all (${lines.length})`}
        </Button>
      </div>}
      <TodayList items={today} />
    </div>
  );
}
